/**
 * "Can this feed read the CRM?" — the keys a feed needs, entered here rather than in a deploy.
 *
 * ## Why this is a screen and not an environment variable
 *
 * Arun, 2026-09-03: *"and shouldnt these creds be part of the api i create? why is it sitting at
 * the env level? for every tenant and every crm or event based hting, it will have its own auth
 * no?"*
 *
 * Right, and the arithmetic is the argument: environment variables grow with *deploys*, these
 * grow with *tenants × integrations*. Two LeadSquared keys for one hospital is a footnote; forty
 * tenants with a CRM each is a deploy config nobody can read, and every new feed needs somebody
 * with Render access before it can fetch a single row. The app already does this one layer up —
 * an account's 8loop API key is typed into a form, encrypted, and shown masked — so a CRM's
 * credentials work the same way.
 *
 * ## What stays in the environment, and why
 *
 * An environment variable *of the same name as the slot* still wins, so an existing deploy keeps
 * working and an incident can be fixed without a browser. When one is set, the field below says
 * so instead of pretending the value it is holding is the one being used — a form that silently
 * saves into an overridden slot is worse than no form.
 *
 * ## What this never shows
 *
 * The value. The server returns a mask (`u$rfake12345…90ab`) and nothing else, so this form
 * never pre-fills and a blank field always means "leave it alone" — never "it is empty". These
 * are keys into somebody else's CRM, holding somebody else's patients.
 */

import { useState } from "react";
import { DateTime } from "luxon";
import { AlertTriangle, CheckCircle2, KeyRound, Loader2, Trash2 } from "lucide-react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { SettingsSection } from "@/components/ui/settings-section";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from "@/components/ui/alert-dialog";
import {
  errorMessage,
  type OrchestratorCredentials,
  type OrchestratorCredentialSlot,
} from "@/lib/api";
import { useOrchestratorClearCredential, useOrchestratorSaveCredentials } from "@/lib/queries";

export function CrmCredentialsSection({
  customer,
  label,
  credentials,
}: {
  customer: string;
  /** The feed's own name, so the copy can say whose CRM this reads. */
  label: string;
  credentials: OrchestratorCredentials;
}) {
  const save = useOrchestratorSaveCredentials(customer);
  const clear = useOrchestratorClearCredential(customer);
  const [draft, setDraft] = useState<Record<string, string>>({});
  /** Set only by pressing Replace. A ready feed folds this away; an unready one cannot. */
  const [replacing, setReplacing] = useState(false);

  /* A feed whose source needs no authentication — an internal service — has nothing to ask
     for, and a card headed with a question it does not have is noise. */
  if (credentials.slots.length === 0) return null;

  const open = replacing || !credentials.ready;
  /* "Yes" is not the whole answer, and saying "stored, encrypted, in this app" over a key that
     is really coming from a Render variable would be a lie the reader cannot see through. */
  const required = credentials.slots.filter((s) => s.required);
  const fromEnv = required.filter((s) => s.source === "environment").length;
  const filled = Object.entries(draft)
    .map(([slot, value]) => [slot, value.trim()] as const)
    .filter(([, value]) => value !== "");

  const submit = () => {
    save.mutate(Object.fromEntries(filled), {
      onSuccess: (res) => {
        setDraft({});
        // Only folds away once every required key is in. Closing the form on a save that
        // left the feed unable to fetch would hide the half that is still missing.
        if (res.ready) setReplacing(false);
        toast.success(res.message);
      },
      onError: (e) => toast.error(errorMessage(e)),
    });
  };

  return (
    <SettingsSection
      title="Can this feed read the CRM?"
      description={
        !credentials.ready
          ? `Not yet. ${label} needs its CRM keys before any run can fetch a row. Get them from the CRM itself — these are not the 8loop API key this account dials with.`
          : fromEnv === 0
            ? "Yes — its keys are stored, encrypted, in this app."
            : fromEnv === required.length
              ? "Yes, but from the server's environment rather than from here. Remove those variables and it falls back to whatever is stored below."
              : "Yes — though not every key comes from this app. The ones set on the server override what is stored here."
      }
      footer={
        open ? (
          <>
            {/* Only when folding away is actually possible. Once a key is removed the form has
                to stay open, and a Cancel that visibly does nothing is worse than no Cancel. */}
            {replacing && credentials.ready ? (
              <Button
                variant="ghost"
                size="sm"
                onClick={() => {
                  setDraft({});
                  setReplacing(false);
                }}
                disabled={save.isPending}
              >
                Cancel
              </Button>
            ) : null}
            {/* Disabled until something is typed, and the reason sits beside it rather than
                in a tooltip: a dead button with no explanation reads as a broken page. */}
            {filled.length === 0 ? (
              <span className="text-muted-foreground text-xs">
                Paste a key above to save.
              </span>
            ) : null}
            <Button size="sm" onClick={submit} disabled={filled.length === 0 || save.isPending}>
              {save.isPending ? (
                <Loader2 className="size-3.5 animate-spin" />
              ) : (
                <KeyRound className="size-3.5" />
              )}
              {filled.length > 1 ? `Save ${filled.length} keys` : "Save"}
            </Button>
          </>
        ) : (
          <Button variant="outline" size="sm" onClick={() => setReplacing(true)}>
            <KeyRound className="size-3.5" />
            Replace a key
          </Button>
        )
      }
    >
      {credentials.slots.map((slot) =>
        open ? (
          <SlotField
            key={slot.slot}
            slot={slot}
            value={draft[slot.slot] ?? ""}
            onChange={(v) => setDraft((d) => ({ ...d, [slot.slot]: v }))}
            onEnter={() => {
              if (filled.length > 0 && !save.isPending) submit();
            }}
            clearing={clear.isPending}
            onClear={() =>
              clear.mutate(slot.slot, {
                onSuccess: (res) => toast.success(res.message),
                onError: (e) => toast.error(errorMessage(e)),
              })
            }
          />
        ) : (
          <SlotSummary key={slot.slot} slot={slot} />
        ),
      )}

      {open ? (
        <p className="text-muted-foreground text-xs">
          Saved encrypted, with the rest of this app's secrets — nobody can read one back out of
          here, including you. Once they are in, use{" "}
          <span className="text-foreground font-medium">Dry run</span> below: it authenticates
          against the live CRM and stores nothing, so a wrong key shows up as a rejected read
          rather than as a quiet empty list.
        </p>
      ) : null}
    </SettingsSection>
  );
}

/** One key in the "yes, it is set" state: what it is, enough of it to recognise, and from where. */
function SlotSummary({ slot }: { slot: OrchestratorCredentialSlot }) {
  /* The tick is per slot, not per section: an *optional* lookup's key can be missing while the
     feed as a whole is ready, and ticking that would be a lie about a field that is blank. */
  const set = slot.source === "stored" || slot.source === "environment";
  return (
    <div className="flex flex-wrap items-baseline justify-between gap-x-4 gap-y-1">
      <div className="flex items-baseline gap-2">
        {set ? (
          <CheckCircle2 className="text-success size-3.5 translate-y-0.5" />
        ) : (
          <AlertTriangle className="text-warning size-3.5 translate-y-0.5" />
        )}
        <span className="text-sm font-medium">{slot.label}</span>
        {slot.masked ? (
          <code className="text-muted-foreground font-mono text-xs">{slot.masked}</code>
        ) : null}
      </div>
      <p className="text-muted-foreground text-xs">
        <SourceLine slot={slot} />
      </p>
    </div>
  );
}

/** One key in the "type it in" state. */
function SlotField({
  slot,
  value,
  onChange,
  onEnter,
  onClear,
  clearing,
}: {
  slot: OrchestratorCredentialSlot;
  value: string;
  onChange: (v: string) => void;
  onEnter: () => void;
  onClear: () => void;
  clearing: boolean;
}) {
  const overridden = slot.source === "environment";
  const quoted = /^["']|["']$/.test(value.trim());
  return (
    <div className="space-y-1.5">
      <div className="flex items-center justify-between gap-3">
        <Label htmlFor={`cred-${slot.slot}`}>
          {slot.label}
          {slot.required ? null : (
            <span className="text-muted-foreground ml-1.5 font-normal">optional</span>
          )}
        </Label>
        {/* Only when there is something to remove. Removing is deliberate and confirmed:
            it stops this feed reading the CRM, which is sometimes exactly the point. */}
        {slot.source === "stored" || slot.source === "unreadable" ? (
          <AlertDialog>
            <AlertDialogTrigger asChild>
              <Button variant="ghost" size="sm" disabled={clearing}>
                <Trash2 className="size-3.5" />
                Remove
              </Button>
            </AlertDialogTrigger>
            <AlertDialogContent>
              <AlertDialogHeader>
                <AlertDialogTitle>Remove the {slot.label.toLowerCase()}?</AlertDialogTitle>
                <AlertDialogDescription>
                  This feed stops reading the CRM until it is entered again — runs will fail
                  rather than quietly return nothing. The value cannot be recovered from here.
                </AlertDialogDescription>
              </AlertDialogHeader>
              <AlertDialogFooter>
                <AlertDialogCancel>Keep it</AlertDialogCancel>
                <AlertDialogAction onClick={onClear}>Remove it</AlertDialogAction>
              </AlertDialogFooter>
            </AlertDialogContent>
          </AlertDialog>
        ) : null}
      </div>
      <Input
        id={`cred-${slot.slot}`}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        onKeyDown={(e) => {
          if (e.key === "Enter") onEnter();
        }}
        /* The mask as the placeholder: it says "there is one already, and this is which one"
           without pre-filling a field whose value we do not have. */
        placeholder={slot.masked ?? "paste it here"}
        className="font-mono text-xs"
        autoComplete="off"
        spellCheck={false}
      />
      {/* Caught rather than stripped: a key could legitimately contain a quote, and silently
          mangling what somebody pasted is worse than telling them. This is a real mistake —
          keys are copied out of `.env` files and JSON, and both bring their quotes along, and
          the only symptom is a 401 from the CRM twelve seconds into a run. */}
      {quoted ? (
        <p className="text-warning text-xs">Looks like it still has quotes around it.</p>
      ) : (
        <p className="text-muted-foreground text-xs">
          {/* What it is for, then where to find it. The second half is the one that makes the
              field answerable by somebody who has never opened this CRM. */}
          {slot.purpose}
          {slot.help ? <> {slot.help}</> : null}
        </p>
      )}
      <p
        className={
          slot.source === "unreadable"
            ? "text-warning flex items-baseline gap-1.5 text-xs"
            : "text-muted-foreground text-xs"
        }
      >
        {slot.source === "unreadable" ? (
          <AlertTriangle className="size-3.5 translate-y-0.5 shrink-0" />
        ) : null}
        <span>
          <SourceLine slot={slot} />
          {overridden ? (
            <> Anything you save here is kept, but not used until that variable is removed.</>
          ) : null}
        </span>
      </p>
    </div>
  );
}

/** Where this key's value is coming from, in a sentence. Shared so both states agree. */
function SourceLine({ slot }: { slot: OrchestratorCredentialSlot }) {
  switch (slot.source) {
    case "stored":
      return (
        <>
          Stored in this app
          {slot.updated_at
            ? ` · saved ${DateTime.fromISO(slot.updated_at).toRelative() ?? ""}`
            : ""}
          {slot.updated_by_name ? ` by ${slot.updated_by_name}` : ""}
        </>
      );
    case "environment":
      return (
        <>
          Set on the server as <code className="font-mono">{slot.slot}</code>, which overrides
          whatever is stored here.
        </>
      );
    case "unreadable":
      return (
        <>
          Stored, but this server cannot decrypt it —{" "}
          <code className="font-mono">CREDENTIAL_ENCRYPTION_KEY</code> has changed since it was
          saved. Paste the key again to fix it.
        </>
      );
    case "missing":
      /* Not "…and without it the feed cannot fetch anything": the purpose line directly above
         already says what breaks, and saying it twice in adjacent sentences reads as a warning
         about two different things. */
      return slot.required ? <>Not set yet.</> : <>Not set, which is allowed.</>;
  }
}
